// Page de messagerie privée
// Liste des conversations et chat

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, ChevronLeft, Search, Image, MoreVertical,
  Check, CheckCheck, User
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Conversation List Item
function ConversationItem({ conversation, isActive, onClick }) {
  const other = conversation.other_participant;
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors ${
        isActive ? 'bg-blue-50' : ''
      }`}
    >
      {other?.profile_photo ? (
        <img
          src={other.profile_photo}
          alt={other.display_name}
          className="w-14 h-14 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
          <User size={24} className="text-blue-600" />
        </div>
      )}
      
      <div className="flex-1 text-left min-w-0">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 truncate">
            {other?.display_name || 'Utilisateur'}
          </h3>
          {conversation.last_message_at && (
            <span className="text-xs text-gray-500">
              {formatTime(conversation.last_message_at)}
            </span>
          )}
        </div>
        {conversation.last_message_preview && (
          <p className="text-sm text-gray-500 truncate">
            {conversation.last_message_preview}
          </p>
        )}
      </div>
    </button>
  );
}

// Message Bubble
function MessageBubble({ message, isOwn }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}
    >
      <div
        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
          isOwn 
            ? 'bg-blue-600 text-white rounded-br-md' 
            : 'bg-gray-100 text-gray-900 rounded-bl-md'
        }`}
      >
        {message.media_url && (
          <img
            src={message.media_url}
            alt=""
            className="max-w-full rounded-lg mb-2"
          />
        )}
        {message.content && (
          <p className="break-words">{message.content}</p>
        )}
        <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
          isOwn ? 'text-blue-200' : 'text-gray-400'
        }`}>
          <span>{formatMessageTime(message.created_at)}</span>
          {isOwn && (
            message.is_read 
              ? <CheckCheck size={14} />
              : <Check size={14} />
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Helper functions
function formatTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function formatMessageTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Main Messages Page
export default function MessagesPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { profile } = useOutletContext() || {};
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [currentConvo, setCurrentConvo] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const token = localStorage.getItem('ecosystem_token');

  useEffect(() => {
    if (!token) {
      navigate('/reseau/connexion');
      return;
    }
    fetchConversations();
  }, [token]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
        
        // If we have a conversationId, set current
        if (conversationId) {
          const convo = data.find(c => c.id === conversationId);
          setCurrentConvo(convo);
        }
      }
    } catch (e) {
      console.error('Error fetching conversations:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convoId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/conversations/${convoId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        
        // Update current convo
        const convo = conversations.find(c => c.id === convoId);
        setCurrentConvo(convo);
      }
    } catch (e) {
      console.error('Error fetching messages:', e);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !conversationId) return;
    
    setSending(true);
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newMessage.trim() })
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          id: data.message_id,
          content: newMessage.trim(),
          sender_id: profile?.id,
          created_at: new Date().toISOString(),
          is_read: false
        }]);
        setNewMessage('');
        inputRef.current?.focus();
      }
    } catch (e) {
      console.error('Error sending message:', e);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (convo) => {
    navigate(`/reseau/messages/${convo.id}`);
    setCurrentConvo(convo);
  };

  const filteredConversations = conversations.filter(c => 
    c.other_participant?.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.other_participant?.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)] flex bg-white">
      {/* Conversations List */}
      <div className={`w-full md:w-96 border-r border-gray-200 flex flex-col ${
        conversationId ? 'hidden md:flex' : 'flex'
      }`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Messages</h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-100 border-0"
            />
          </div>
        </div>
        
        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-gray-500">Aucune conversation</p>
              <p className="text-sm text-gray-400 mt-2">
                Suivez des utilisateurs et envoyez-leur des messages
              </p>
            </div>
          ) : (
            filteredConversations.map((convo) => (
              <ConversationItem
                key={convo.id}
                conversation={convo}
                isActive={convo.id === conversationId}
                onClick={() => selectConversation(convo)}
              />
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${
        conversationId ? 'flex' : 'hidden md:flex'
      }`}>
        {conversationId && currentConvo ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 flex items-center gap-3">
              <button
                onClick={() => navigate('/reseau/messages')}
                className="md:hidden p-2 -ml-2"
              >
                <ChevronLeft size={24} />
              </button>
              
              {currentConvo.other_participant?.profile_photo ? (
                <img
                  src={currentConvo.other_participant.profile_photo}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <User size={20} className="text-blue-600" />
                </div>
              )}
              
              <div className="flex-1">
                <h2 className="font-semibold text-gray-900">
                  {currentConvo.other_participant?.display_name}
                </h2>
                <p className="text-sm text-gray-500">
                  @{currentConvo.other_participant?.username}
                </p>
              </div>
              
              <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                <MoreVertical size={20} />
              </button>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isOwn={msg.sender_id === profile?.id}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  <Image size={20} />
                </button>
                
                <Input
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Écrire un message..."
                  className="flex-1"
                  data-testid="message-input"
                />
                
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-blue-600 hover:bg-blue-700"
                  size="icon"
                >
                  <Send size={18} />
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center px-4">
            <div>
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send size={32} className="text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Tes messages
              </h2>
              <p className="text-gray-500">
                Envoie des messages privés aux utilisateurs que tu suis
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
